import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  Users as UsersIcon, RefreshCw, Mail, Calendar, Shield, UserCheck, UserX, Eye, Clock, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import { PageHeader, MetricRow, FilterBar, DataTable } from '../design';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
const formatDateTime = (dateStr) => {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ROLE_TONE = {
  owner: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  admin: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  manager: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  user: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  governor: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  superadmin: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};
const roleTone = (role) => ROLE_TONE[role?.toLowerCase()] || ROLE_TONE.user;

function StatusBadge({ status }) {
  if (status === 'active') {
    return <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Active</Badge>;
  }
  return <Badge variant="outline" className="bg-rose-500/10 text-rose-400 border-rose-500/20">{status || 'Inactive'}</Badge>;
}

export default function Users() {
  const { isAuthenticated } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orphans, setOrphans] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await governanceAPI.getUsers();
      setUsers(res.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrphans = useCallback(async () => {
    try {
      const res = await governanceAPI.getOrphanUsers();
      setOrphans(res.data || null);
    } catch (e) {
      setOrphans(null);
    }
  }, []);

  useEffect(() => { if (isAuthenticated) { fetchUsers(); fetchOrphans(); } }, [isAuthenticated, fetchUsers, fetchOrphans]);

  const tenantOptions = useMemo(() => {
    const m = new Map();
    users.forEach((u) => { if (u.tenant_id) m.set(u.tenant_id, u.tenant_name || u.tenant_id); });
    return Array.from(m.entries()).map(([value, label]) => ({ value, label }));
  }, [users]);

  const roleOptions = useMemo(() => {
    const s = new Set(users.map((u) => u.role).filter(Boolean));
    return Array.from(s).map((r) => ({ value: r, label: r.charAt(0).toUpperCase() + r.slice(1) }));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return users.filter((u) => {
      const matchesSearch = !q ||
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) ||
        u.tenant_name?.toLowerCase().includes(q) || u.org_name?.toLowerCase().includes(q);
      const matchesTenant = tenantFilter === 'all' || u.tenant_id === tenantFilter;
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || u.status === statusFilter;
      return matchesSearch && matchesTenant && matchesRole && matchesStatus;
    });
  }, [users, searchQuery, tenantFilter, roleFilter, statusFilter]);

  const stats = useMemo(() => ({
    total: users.length,
    active: users.filter((u) => u.status === 'active').length,
    inactive: users.filter((u) => u.status !== 'active').length,
    admins: users.filter((u) => u.role === 'admin' || u.role === 'owner' || u.role === 'superadmin').length,
  }), [users]);

  const metrics = [
    { key: 'total-users', label: 'Total Users', value: stats.total, icon: UsersIcon },
    { key: 'active-users', label: 'Active', value: stats.active, icon: UserCheck, tone: 'success' },
    { key: 'inactive-users', label: 'Inactive', value: stats.inactive, icon: UserX, tone: 'danger' },
    { key: 'admin-users', label: 'Admins / Owners', value: stats.admins, icon: Shield, tone: 'accent' },
  ];

  const columns = [
    { accessorKey: 'name', header: 'User', cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium gov-text-primary">{row.original.name || 'N/A'}</span>
        <span className="text-xs gov-text-tertiary flex items-center gap-1"><Mail className="h-3 w-3" />{row.original.email}</span>
      </div>
    ) },
    { accessorKey: 'tenant_name', header: 'Tenant / Org', cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="text-sm gov-text-secondary">{row.original.tenant_name || 'N/A'}</span>
        <span className="text-xs gov-text-tertiary">{row.original.org_name || 'N/A'}</span>
      </div>
    ) },
    { accessorKey: 'role', header: 'Role', cell: ({ row }) => (
      <Badge variant="outline" className={cn('text-xs capitalize', roleTone(row.original.role))}>{row.original.role || 'user'}</Badge>
    ) },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} /> },
    { accessorKey: 'last_login', header: 'Last Login', cell: ({ row }) => (
      <span className="flex items-center gap-1 text-sm gov-text-tertiary"><Clock className="h-3 w-3" />{formatDateTime(row.original.last_login)}</span>
    ) },
    { accessorKey: 'created_at', header: 'Created', cell: ({ row }) => (
      <span className="flex items-center gap-1 text-sm gov-text-tertiary"><Calendar className="h-3 w-3" />{formatDate(row.original.created_at)}</span>
    ) },
    { id: 'actions', header: '', cell: ({ row }) => (
      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/governance/users/${row.original.id}`); }} data-testid={`view-user-${row.original.id}`}>
        <Eye className="h-4 w-4 mr-1" /> View
      </Button>
    ) },
  ];

  return (
    <div className="space-y-8" data-testid="users-page">
      <PageHeader
        title="All Users"
        subtitle="Read-only view of all users across all organizations"
        actions={
          <Button variant="outline" size="sm" onClick={fetchUsers} data-testid="refresh-users">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        }
      />

      <MetricRow metrics={metrics} cols={4} />

      {orphans && (
        <div
          className={cn(
            'rounded-xl border p-4',
            orphans.fixable_sub_users > 0
              ? 'bg-rose-500/5 border-rose-500/30'
              : 'bg-emerald-500/5 border-emerald-500/25'
          )}
          data-testid="orphan-users-report"
        >
          <div className="flex items-start gap-3">
            {orphans.fixable_sub_users > 0
              ? <AlertTriangle className="h-5 w-5 text-rose-400 mt-0.5 shrink-0" />
              : <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium gov-text-primary">
                Phantom-tenant check —{' '}
                {orphans.fixable_sub_users > 0
                  ? `${orphans.fixable_sub_users} sub-user(s) missing a tenant`
                  : 'no orphaned sub-users'}
              </div>
              <p className="text-xs gov-text-tertiary mt-0.5">
                {orphans.missing_tenant_id} of {orphans.total_users} users have no tenant_id
                ({orphans.root_users} root account(s), {orphans.fixable_sub_users} fixable sub-user(s)).
                Sub-users without a tenant read/write under a phantom tenant and see none of their org's data.
              </p>
              {orphans.fixable_sub_users > 0 && (
                <div className="mt-3 space-y-1.5">
                  {orphans.orphans.filter((o) => !o.is_root).map((o) => (
                    <div key={o.user_id} className="flex items-center gap-2 text-xs" data-testid={`orphan-row-${o.user_id}`}>
                      <Badge variant="outline" className={cn('text-[10px]', roleTone(o.role))}>{o.role || 'user'}</Badge>
                      <span className="gov-text-secondary truncate">{o.email}</span>
                      <span className="gov-text-tertiary">→ should resolve to tenant</span>
                      <code className="gov-text-primary">{o.resolves_to_tenant}</code>
                      {o.is_phantom_split && (
                        <Badge variant="outline" className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-[10px]">phantom split</Badge>
                      )}
                    </div>
                  ))}
                  <p className="text-[11px] gov-text-tertiary pt-1 font-mono">{orphans.remediation}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <FilterBar
        search={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search by name, email, tenant, or organization…' }}
        filters={[
          { key: 'tenant', label: 'Tenants', value: tenantFilter, onChange: setTenantFilter, options: tenantOptions, defaultLabel: 'All Tenants' },
          { key: 'role', label: 'Roles', value: roleFilter, onChange: setRoleFilter, options: roleOptions, defaultLabel: 'All Roles' },
          { key: 'status', label: 'Status', value: statusFilter, onChange: setStatusFilter, options: [
            { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'suspended', label: 'Suspended' },
          ], defaultLabel: 'All Status' },
        ]}
        resultCount={filteredUsers.length}
        onReset={() => { setSearchQuery(''); setTenantFilter('all'); setRoleFilter('all'); setStatusFilter('all'); }}
      />

      <DataTable
        columns={columns}
        data={filteredUsers}
        loading={loading}
        testId="users-table"
        onRowClick={(u) => navigate(`/governance/users/${u.id}`)}
        emptyIcon={UsersIcon}
        emptyTitle="No users found"
        emptyHint={searchQuery || tenantFilter !== 'all' || roleFilter !== 'all' || statusFilter !== 'all'
          ? 'No users match your current filters. Try adjusting your search criteria.'
          : 'No users are registered in the system yet.'}
        pageSize={25}
      />
    </div>
  );
}
